import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { Heading } from '@acorns/web-components'
import styled from 'styled-components'

import FullscreenTakeover from 'components/FullscreenTakeover'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { CloseAccountWrapper } from 'pages/close-account-v3/closeAccountWrapper'
import {
  StyledBody,
  StyledCard,
  StyledCloseAccountButton,
} from 'pages/close-account-v3/components'
import { style } from 'theme'

const BodyContainer = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: auto;
    min-height: 50%;
    align-self: center;
    padding-top: 50px;
  }
`
const ButtonContainer = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};

  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    position: relative;
    width: 100%;
  }
`
const Footnote = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  padding-top: 50px;
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 16px;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 30px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding-bottom: 20px;
    border-top: none;
    margin-top: 0;
    margin-bottom: 20px;
    padding-top: 0;
    margin: 0 auto;
  }
`

const Card = styled(StyledCard)`
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    margin-top: 30px;
  }
`

const CardComponent = styled(StyledCard)<{ innerHeight?: number }>`
  display: flex;
  justify-content: space-between;
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
`

const StyledHeading = styled(Heading)`
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  text-align: center;
  margin: 20px 0;
`

const Body = styled(StyledBody)`
  margin: 0 auto;
`

const StyledIcon = styled(AcornsIcon)`
  height: 104px;
  width: 104px;
  margin: 0 auto;
  display: inline-block;
`
type HandlerProps = {
  handleDone: () => void
}

type Props = {
  hasBalance: boolean
  isWebView: boolean
}

type MergedProps = HandlerProps & Props

export const ConfirmationScreen: React.FC<MergedProps> = ({
  handleDone,
  hasBalance,
  isWebView,
}) => {
  const { height } = useWindowDimensions()
  return (
    <>
      {isWebView && (
        <FullscreenTakeover innerHeight={height}>
          <CloseAccountWrapper
            isWebView={isWebView}
            link={{ text: 'Done', onClick: handleDone }}
            action={{ shouldShowCloseIcon: true, onClick: handleDone }}
            actionStripTitle={undefined}
            cancelLink={true}
            handleCancel={handleDone}
            closeIcon={true}
          >
            {hasBalance ? (
              <CardComponent innerHeight={height} isWebView={isWebView}>
                <BodyContainer>
                  <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />

                  <StyledHeading>Subscription change requested</StyledHeading>
                  <Body>
                    Once the transfers have processed, we'll close your accounts
                    and change your subscription. This usually takes a few days.
                  </Body>
                </BodyContainer>

                <ButtonContainer>
                  <Footnote>
                    Depending on your billing date, you may still be charged the
                    old fee until the downgrade has fully processed.
                  </Footnote>
                  <StyledCloseAccountButton onPress={handleDone}>
                    Done
                  </StyledCloseAccountButton>
                </ButtonContainer>
              </CardComponent>
            ) : (
              <CardComponent innerHeight={height} isWebView={isWebView}>
                <BodyContainer>
                  <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                  <StyledHeading>Subscription change requested</StyledHeading>
                  <StyledBody>
                    We'll start processing your request and let you know as soon
                    as your account is closed.
                  </StyledBody>
                </BodyContainer>
                <ButtonContainer>
                  <Footnote>
                    Depending on your billing date, you may still be charged the
                    old fee until the downgrade has fully processed.
                  </Footnote>
                  <StyledCloseAccountButton onPress={handleDone}>
                    Done
                  </StyledCloseAccountButton>
                </ButtonContainer>
              </CardComponent>
            )}
          </CloseAccountWrapper>
        </FullscreenTakeover>
      )}
      {!isWebView && (
        <>
          {hasBalance ? (
            <Card isWebView={isWebView}>
              <BodyContainer>
                <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                <StyledHeading>Subscription change requested</StyledHeading>
                <Body>
                  Once the transfers have processed, we'll close your accounts
                  and cancel your subscription.
                </Body>

                <Footnote>
                  Depending on your billing date, you may still be charged the
                  old fee until the downgrade has fully processed.
                </Footnote>
                <StyledCloseAccountButton onPress={handleDone}>
                  Done
                </StyledCloseAccountButton>
              </BodyContainer>
            </Card>
          ) : (
            <StyledCard isWebView={isWebView}>
              <BodyContainer>
                <StyledIcon icon={AcornsIcon.Icon.AcornsPending} />
                <StyledHeading>Subscription change requested</StyledHeading>
                <StyledBody>
                  We'll start processing your request and let you know as soon
                  as your account is closed.
                </StyledBody>
                <Footnote>
                  Depending on your billing date, you may still be charged the
                  old fee until the downgrade has fully processed.
                </Footnote>
                <StyledCloseAccountButton onPress={handleDone}>
                  Done
                </StyledCloseAccountButton>
              </BodyContainer>
            </StyledCard>
          )}
        </>
      )}
    </>
  )
}
